import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import {
  HiOutlineChartBar,
  HiOutlineClipboardDocumentList,
  HiOutlineUserGroup,
  HiOutlineCalendarDays,
  HiOutlineArrowPath,
  HiOutlineServerStack,
  HiOutlineExclamationTriangle,
  HiOutlineBellAlert,
  HiOutlineChatBubbleLeftRight,
  HiOutlineWrenchScrewdriver,
  HiOutlineCheckCircle,
} from "react-icons/hi2";
import Chart from "react-apexcharts";
import { getAuth } from "firebase/auth";
import { fetchAdminClientsSnapshot } from "../../services/adminClientsApi";
import { listAdminUnreadConversations } from "../../api/backend";
import { getScheduleTypeMeta } from "../../services/scheduleEvents";
import { useNotifications } from "../../hooks/useNotifications";

const ATTENTION_STYLES = {
  renewal_overdue: { icon: HiOutlineExclamationTriangle, color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-200' },
  renewal_soon: { icon: HiOutlineBellAlert, color: 'text-amber-500', bg: 'bg-amber-50', border: 'border-amber-200' },
  renewal_upcoming: { icon: HiOutlineCalendarDays, color: 'text-sky-500', bg: 'bg-sky-50', border: 'border-sky-200' },
  message: { icon: HiOutlineChatBubbleLeftRight, color: 'text-sky-500', bg: 'bg-sky-50', border: 'border-sky-200' },
  ticket: { icon: HiOutlineWrenchScrewdriver, color: 'text-rose-500', bg: 'bg-rose-50', border: 'border-rose-200' },
};

// Estado del último aviso de renovación mandado para ese cliente — "pending"
// no es alarma (todavía no le toca ningún aviso en este ciclo), "failed" sí.
const DELIVERY_BADGES = {
  sent: { label: '✅ Enviado', className: 'bg-emerald-100 text-emerald-700' },
  failed: { label: '⚠️ No enviado', className: 'bg-red-100 text-red-700' },
  pending: { label: 'Sin aviso aún', className: 'bg-gray-100 text-gray-500' },
};

const WORKFLOW_NOTIFICATION_META = {
  lead_new: { label: 'Leads nuevos', tone: 'blue' },
  lead_inbound: { label: 'Leads que respondieron', tone: 'violet' },
  client_inbound: { label: 'Mensajes de clientes', tone: 'sky' },
  hosting_renewal: { label: 'Renovaciones', tone: 'amber' },
  hosting_invoice_created: { label: 'Facturas de hosting', tone: 'emerald' },
  monthly_invoice_created: { label: 'Facturas mensuales', tone: 'emerald' },
  monthly_service_reminder: { label: 'Mensualidades', tone: 'amber' },
};

function WorkflowKpiPanel() {
  const { notifications, actionRequired, markAttended } = useNotifications();
  const [active, setActive] = useState('action');
  // Cerrar/leer una notificación también la resuelve para este tablero. Antes
  // este panel ignoraba `read` y volvía a mostrarla después de refrescar.
  const items = notifications.filter((item) => !item.read && !item.attendedAt);
  const groups = {
    action: actionRequired,
    leads: items.filter((item) => ['lead_new', 'lead_inbound'].includes(item.type)),
    messages: items.filter((item) => item.type === 'client_inbound'),
    renewals: items.filter((item) => /^(hosting_|monthly_)/.test(String(item.type || ''))),
  };
  const cards = [
    ['action', 'Atención requerida', groups.action.length, 'rose'],
    ['leads', 'Leads por atender', groups.leads.length, 'violet'],
    ['messages', 'Mensajes de clientes', groups.messages.length, 'sky'],
    ['renewals', 'Renovaciones, mensualidades y facturas', groups.renewals.length, 'amber'],
  ];
  const tone = { rose: 'border-rose-200 bg-rose-50 text-rose-800', violet: 'border-violet-200 bg-violet-50 text-violet-800', sky: 'border-sky-200 bg-sky-50 text-sky-800', amber: 'border-amber-200 bg-amber-50 text-amber-800' };
  const selected = groups[active] || [];
  const formatDate = (value) => {
    const date = value?.toDate?.() || (value?.seconds ? new Date(value.seconds * 1000) : null);
    return date ? date.toLocaleString('es-PR', { dateStyle: 'short', timeStyle: 'short' }) : 'Ahora';
  };
  return <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
    <div className="flex flex-wrap items-start justify-between gap-3"><div><h2 className="text-lg font-semibold text-gray-800">Indicadores operativos</h2><p className="mt-1 text-sm text-gray-500">Cada número abre exactamente las notificaciones que lo componen; no son estimados ni contadores aislados.</p></div><Link to="/admin/whatsapp" className="text-sm font-semibold text-orange-600 hover:underline">Abrir mensajería →</Link></div>
    <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{cards.map(([key, label, value, color]) => <button key={key} type="button" onClick={() => setActive(key)} className={`rounded-xl border p-4 text-left transition ${tone[color]} ${active === key ? 'ring-2 ring-offset-1 ring-gray-300' : 'hover:brightness-95'}`}><p className="text-xs font-bold uppercase tracking-wide">{label}</p><p className="mt-2 text-3xl font-black">{value}</p><p className="mt-1 text-xs">Ver qué lo está notificando</p></button>)}</div>
    <div className="mt-4 overflow-hidden rounded-xl border border-gray-100"><div className="border-b bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-700">{cards.find(([key]) => key === active)?.[1]} ({selected.length})</div>{selected.length ? <ul className="divide-y divide-gray-100">{selected.slice(0, 10).map((item) => <li key={item.id} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"><div className="min-w-0"><p className="text-sm font-semibold text-gray-800">{item.message || 'Actualización del sistema'}</p><p className="mt-1 text-xs text-gray-400">{WORKFLOW_NOTIFICATION_META[item.type]?.label || item.type || 'Sistema'} · {formatDate(item.createdAt)}</p></div><div className="flex shrink-0 items-center gap-3 text-xs"><Link to={item.route || '/admin'} className="font-bold text-orange-600 hover:underline">Ver origen</Link>{item.requiresAction && <button type="button" onClick={() => markAttended(item.id)} className="font-semibold text-gray-600 hover:text-gray-900">Marcar atendida</button>}</div></li>)}</ul> : <p className="px-4 py-6 text-sm text-gray-500">No hay alertas pendientes en este indicador.</p>}</div>
  </section>;
}

// Un solo bloque de "esto necesita tu atención hoy" — antes esto estaba
// repartido en 3 lugares (lista de renovaciones aparte, tarjeta de mensajes
// sin leer en Clientes, contador de tickets en su propia tarjeta). Los CRMs
// grandes (HubSpot, Salesforce) lo juntan todo en un solo panel arriba, no en
// piezas sueltas — esto imita eso.
const MAX_ATTENTION_ACKS = 120;

function AttentionTodayPanel({ userId }) {
  const [items, setItems] = useState(null); // null = cargando
  const [acknowledged, setAcknowledged] = useState(new Set());

  useEffect(() => {
    let active = true;
    if (!userId) {
      setAcknowledged(new Set());
      return () => { active = false; };
    }
    getDoc(doc(db, 'users', userId))
      .then((snap) => {
        if (!active) return;
        const keys = Array.isArray(snap.data()?.dashboardAcknowledgedAttention)
          ? snap.data().dashboardAcknowledgedAttention
          : [];
        setAcknowledged(new Set(keys));
      })
      .catch((error) => console.error('[AttentionTodayPanel] preferencias:', error.message));
    return () => { active = false; };
  }, [userId]);

  const markAttentionHandled = (key) => {
    if (!key || !userId) return;
    setAcknowledged((current) => {
      if (current.has(key)) return current;
      const next = new Set(current);
      next.add(key);
      setDoc(doc(db, 'users', userId), {
        dashboardAcknowledgedAttention: Array.from(next).slice(-MAX_ATTENTION_ACKS),
      }, { merge: true }).catch((error) => console.error('[AttentionTodayPanel] guardar atendida:', error.message));
      return next;
    });
  };

  useEffect(() => {
    Promise.all([
      fetchAdminClientsSnapshot().then((res) => res.clients || []).catch(() => []),
      listAdminUnreadConversations().catch(() => []),
      getDocs(collectionGroup(db, 'serviceTickets')).catch(() => null),
      getDocs(query(collection(db, 'hosting_reminder_log'), orderBy('sentAt', 'desc'), limit(200))).catch(() => null),
    ]).then(([clients, unread, ticketsSnap, logsSnap]) => {
      const now = Date.now();

      // Un solo aviso por cliente: el más reciente. Como el query ya viene
      // ordenado por sentAt desc, el primero que encontramos por clientId es
      // el último envío — no hace falta comparar fechas.
      const latestLogByClient = {};
      (logsSnap?.docs || []).forEach((d) => {
        const log = d.data();
        if (log.clientId && !latestLogByClient[log.clientId]) latestLogByClient[log.clientId] = log;
      });

      const renewalItems = clients
        .filter((c) => c.hostingRenewalDate && c.status !== 'deleted')
        .map((c) => {
          const date = c.hostingRenewalDate?.toDate ? c.hostingRenewalDate.toDate() : new Date(c.hostingRenewalDate);
          const days = Math.ceil((date.getTime() - now) / (24 * 60 * 60 * 1000));
          const cycleKey = date.toISOString().slice(0, 10);
          const log = latestLogByClient[c.id];
          const logIsCurrentCycle = log?.renewalDate?.toDate
            && log.renewalDate.toDate().toISOString().slice(0, 10) === cycleKey;
          // pending = todavía no le toca ningún aviso en este ciclo (normal,
          // no es una alarma). sent/failed sí reflejan un envío real.
          const deliveryStatus = !logIsCurrentCycle ? 'pending' : (log.emailSuccess ? 'sent' : 'failed');
          return {
            // Una renovación atendida se oculta para este ciclo, pero vuelve
            // a aparecer automáticamente cuando llega la siguiente fecha.
            key: `renewal_${c.id}_${cycleKey}`,
            type: days < 0 ? 'renewal_overdue' : days <= 7 ? 'renewal_soon' : 'renewal_upcoming',
            days,
            title: c.businessName || c.name || 'Cliente',
            detail: days < 0 ? `Venció hace ${Math.abs(days)}d` : days === 0 ? 'Vence hoy' : `Vence en ${days}d`,
            route: '/admin/clients',
            deliveryStatus,
            deliveryError: logIsCurrentCycle ? log.emailError : null,
          };
        })
        .filter((r) => r.days <= 30);

      const messageItems = (unread || []).map((m) => ({
        // Un mensaje nuevo genera otro id y vuelve a requerir atención; no
        // se oculta por haber atendido un mensaje anterior del mismo cliente.
        key: `msg_${m.clientId || m.clientEmail}_${m.latestMessage?.id || m.latestMessage?.timestamp?.seconds || 'actual'}`,
        type: 'message',
        days: -1,
        title: m.clientEmail || 'Cliente',
        detail: (m.body || m.latestMessage?.body || 'Mensaje nuevo').slice(0, 60),
        route: `/admin/clients/${encodeURIComponent(m.clientId || m.clientEmail)}/email`,
      }));

      const ticketItems = (ticketsSnap?.docs || [])
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((t) => (t.priority || '').toLowerCase() === 'critica' && ['abierto', 'en_proceso', 'en_espera_cliente'].includes((t.status || '').toLowerCase()))
        .map((t) => ({
          key: `ticket_${t.id}_${t.updatedAt?.seconds || t.updatedAt?.toMillis?.() || 'actual'}`,
          type: 'ticket',
          days: -1,
          title: t.title || 'Ticket crítico',
          detail: t.clientName || t.clientEmail || 'Servicio',
          route: '/admin/services',
        }));

      const all = [...renewalItems, ...ticketItems, ...messageItems].sort((a, b) => a.days - b.days);
      setItems(all);
    });
  }, []);

  if (items === null) {
    return <div className="rounded-2xl border border-gray-200 bg-white p-5 text-sm text-gray-400">Revisando qué necesita atención…</div>;
  }
  const visibleItems = items.filter((item) => !acknowledged.has(item.key));
  if (visibleItems.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-sm font-semibold text-emerald-700">
        <HiOutlineCheckCircle className="h-5 w-5" />
        Todo al día — nada requiere tu atención ahora mismo.
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5">
      <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500">Atención hoy ({visibleItems.length})</h2>
      <ul className="mt-3 space-y-1.5">
        {visibleItems.slice(0, 10).map((item) => {
          const style = ATTENTION_STYLES[item.type];
          const Icon = style.icon;
          const delivery = DELIVERY_BADGES[item.deliveryStatus];
          return (
            <li key={item.key}>
              <div className={`flex items-center gap-2 rounded-xl border ${style.border} ${style.bg} px-3 py-2 text-sm`}>
                <Link to={item.route} className="flex min-w-0 flex-1 items-center gap-2 hover:brightness-95">
                  <Icon className={`h-4 w-4 flex-shrink-0 ${style.color}`} />
                  <span className="truncate font-semibold text-gray-800">{item.title}</span>
                  <span className="truncate text-gray-500">— {item.detail}</span>
                </Link>
                {delivery && <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${delivery.className}`} title={item.deliveryError || undefined}>{delivery.label}</span>}
                <button type="button" onClick={() => markAttentionHandled(item.key)} className="flex-shrink-0 rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-50">Atendida</button>
              </div>
            </li>
          );
        })}
      </ul>
      {visibleItems.length > 10 && <p className="mt-2 text-xs text-gray-400">+{visibleItems.length - 10} más</p>}
    </div>
  );
}

// Mismas tarjetas de "MÉTRICAS PRINCIPALES" (borde de color + número +
// ícono) — no inventa un estilo nuevo, solo suma 3 más a esa misma fila:
// cuántos dominios hay en total, cuántas renovaciones ya vencieron, y
// cuántas están por vencer en la próxima semana.
function HostingMetricCards() {
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    Promise.all([
      getDocs(collection(db, 'hosting_packages')).then((snap) => snap.size),
      fetchAdminClientsSnapshot().then((res) => res.clients || []),
    ])
      .then(([totalDomains, clients]) => {
        const now = Date.now();
        let overdue = 0;
        let dueSoon = 0;
        clients.forEach((c) => {
          if (!c.hostingRenewalDate || c.status === 'deleted') return;
          const date = c.hostingRenewalDate?.toDate ? c.hostingRenewalDate.toDate() : new Date(c.hostingRenewalDate);
          const days = Math.ceil((date.getTime() - now) / (24 * 60 * 60 * 1000));
          if (days < 0) overdue += 1;
          else if (days <= 30) dueSoon += 1;
        });
        setMetrics({ totalDomains, overdue, dueSoon });
      })
      .catch((err) => console.error('[HostingMetricCards]', err.message));
  }, []);

  if (!metrics) return null;

  return (
    <>
      <div className="bg-white p-5 rounded-lg shadow hover:shadow-lg transition-all duration-200 border-l-4 border-sky-500 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">Dominios de Hosting</p>
          <p className="text-2xl font-bold text-gray-700">{metrics.totalDomains}</p>
        </div>
        <HiOutlineServerStack className="text-sky-500 w-8 h-8" />
      </div>

      <div className="bg-white p-5 rounded-lg shadow hover:shadow-lg transition-all duration-200 border-l-4 border-red-500 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">Renovaciones Vencidas</p>
          <p className="text-2xl font-bold text-gray-700">{metrics.overdue}</p>
        </div>
        <HiOutlineExclamationTriangle className="text-red-500 w-8 h-8" />
      </div>

      <div className="bg-white p-5 rounded-lg shadow hover:shadow-lg transition-all duration-200 border-l-4 border-amber-500 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">Por Vencer (30 días)</p>
          <p className="text-2xl font-bold text-gray-700">{metrics.dueSoon}</p>
        </div>
        <HiOutlineBellAlert className="text-amber-500 w-8 h-8" />
      </div>
    </>
  );
}

const MAX_ACTIVITY_PREFS = 80;
const buildActivityKey = (activity) => {
  const timestamp = activity?.createdAt instanceof Date ? activity.createdAt.getTime() : Date.now();
  return `${activity?.id || activity?.summary || "activity"}_${timestamp}`;
};

const AdminDashboard = () => {
  const auth = getAuth();
  const [user, setUser] = useState(() => auth.currentUser);
  const [stats, setStats] = useState({
    proposalsSent: 0,
    proposalsAccepted: 0,
    clients: 0,
    leads: 0,
    totalInvoiced: 0,
    totalDeposits: 0,
    totalCollected: 0,
  });

  const [chartData, setChartData] = useState({
    series: [{ name: "Ventas", data: [] }],
    options: {
      chart: { type: "area", toolbar: { show: false } },
      xaxis: { categories: [] },
      colors: ["#2563eb"],
      stroke: { curve: "smooth" },
    },
  });

  const [leadMetrics, setLeadMetrics] = useState(null);
  const [sellerPerformance, setSellerPerformance] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [serviceStats, setServiceStats] = useState({ total: 0 });
  const [refreshKey, setRefreshKey] = useState(0);
  const [readActivities, setReadActivities] = useState(new Set());
  const [dismissedActivities, setDismissedActivities] = useState(new Set());
  const [showReadActivities, setShowReadActivities] = useState(false);
  const [leadListExpanded, setLeadListExpanded] = useState(false);
  const formatMoney = (value = 0) =>
    Number(value || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  const formatFullDate = (value) => {
    if (!value) return "Nunca";
    const date =
      value?.toDate?.() ||
      (typeof value === "object" && typeof value.seconds === "number"
        ? new Date(value.seconds * 1000)
        : new Date(value));
    if (!(date instanceof Date) || isNaN(date.getTime())) return "Nunca";
    return date.toLocaleString("es-PR");
  };

  const THIS_YEAR = new Date().getFullYear();
  const LAST_YEAR = THIS_YEAR - 1;
  const MONTH_LABELS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

  const [yearComparison, setYearComparison] = useState({
    invoiced: { current: 0, last: 0 },
    collected: { current: 0, last: 0 },
    invoiceCount: { current: 0, last: 0 },
    monthlyCollected: { current: Array(12).fill(0), last: Array(12).fill(0) },
    monthlyInvoiced: { current: Array(12).fill(0), last: Array(12).fill(0) },
  });

  const [events, setEvents] = useState([]);
  const [aiInsights, setAIInsights] = useState({
    totalLeads: 0,
    hotLeads: 0,
    avgScore: 0,
    topService: "-",
    conversionRate: 0,
  });

  const persistActivityPrefs = useCallback(
    async (field, entries) => {
      if (!user?.uid) return;
      try {
        const userRef = doc(db, "users", user.uid);
        await setDoc(
          userRef,
          {
            [field]: entries.slice(-MAX_ACTIVITY_PREFS),
          },
          { merge: true }
        );
      } catch (err) {
        console.error(`⚠️ No se pudieron guardar ${field}:`, err);
      }
    },
    [user?.uid]
  );

  const persistReadActivity = useCallback(
    (entries) => persistActivityPrefs("dashboardReadActivities", entries),
    [persistActivityPrefs]
  );

  const persistDismissedActivity = useCallback(
    (entries) => persistActivityPrefs("dashboardDismissedActivities", entries),
    [persistActivityPrefs]
  );

  useEffect(() => {
    let active = true;
    const loadPreferences = async () => {
      if (!user?.uid) {
        if (active) {
          setReadActivities(new Set());
          setDismissedActivities(new Set());
        }
        return;
      }
      try {
        const userRef = doc(db, "users", user.uid);
        const snap = await getDoc(userRef);
        if (!active) return;
        const data = snap.exists() ? snap.data() : {};
        const readKeys = Array.isArray(data?.dashboardReadActivities)
          ? data.dashboardReadActivities
          : [];
        const dismissedKeys = Array.isArray(data?.dashboardDismissedActivities)
          ? data.dashboardDismissedActivities
          : [];
        setReadActivities(new Set(readKeys));
        setDismissedActivities(new Set(dismissedKeys));
      } catch (err) {
        console.error("⚠️ No se pudieron cargar preferencias del dashboard:", err);
      }
    };

    loadPreferences();
    return () => {
      active = false;
    };
  }, [user?.uid]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
      setUser(firebaseUser);
    });
    return unsubscribe;
  }, [auth]);

  // 🔹 Función auxiliar: encuentra el servicio más solicitado
  const findTopService = (leads) => {
    const serviceCount = {};
    leads.forEach((lead) => {
      (lead.cardInformation || []).forEach((service) => {
        serviceCount[service] = (serviceCount[service] || 0) + 1;
      });
    });
    const sorted = Object.entries(serviceCount).sort((a, b) => b[1] - a[1]);
    return sorted.length ? sorted[0][0] : "-";
  };
  useEffect(() => {
    const loadData = async () => {
      try {
        const [clientsSnap, invoicesSnap, leadsSnap, proposalsSnap, eventsSnap, sellersSnap, timelineSnap, ticketsSnap] = await Promise.all([
          getDocs(collection(db, "clients")),
          getDocs(collectionGroup(db, "invoices")),
          getDocs(collection(db, "leads")),
          getDocs(collection(db, "proposalTokens")),
          getDocs(collection(db, "companyScheduleEvents")),
          getDocs(collection(db, "salesAgents")),
          getDocs(query(collectionGroup(db, "timeline"), orderBy("createdAt", "desc"), limit(8))),
          getDocs(collectionGroup(db, 'serviceTickets')),
        ]);
  
        let totalInvoiced = 0;
        let totalDeposits = 0;
        let totalCollected = 0;
        const monthlyTotals = {};
        const yearComp = {
          invoiced: { current: 0, last: 0 },
          collected: { current: 0, last: 0 },
          invoiceCount: { current: 0, last: 0 },
          monthlyCollected: { current: Array(12).fill(0), last: Array(12).fill(0) },
          monthlyInvoiced: { current: Array(12).fill(0), last: Array(12).fill(0) },
        };

        const leadsDetailed = leadsSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const sellerDocs = sellersSnap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        const sellerEmailMap = {};
        const sellerTotals = {};
        sellerDocs.forEach((seller) => {
          const emailKey = (seller.email || "").toLowerCase();
          if (emailKey) sellerEmailMap[emailKey] = seller.id;
          sellerTotals[seller.id] = {
            id: seller.id,
            name: seller.name || "Sin nombre",
            email: seller.email || "-",
            quota: Number(seller.quota || 0),
            leads: 0,
            hot: 0,
            active: 0,
            won: 0,
            revenue: 0,
            collected: 0,
          };
        });

        leadsDetailed.forEach((lead) => {
          const emailKey = (lead.assignedToEmail || "").toLowerCase();
          const sellerId =
            (lead.assignedTo && sellerTotals[lead.assignedTo]) ||
            (emailKey && sellerTotals[sellerEmailMap[emailKey]])
              ? lead.assignedTo && sellerTotals[lead.assignedTo]
                ? lead.assignedTo
                : sellerEmailMap[emailKey]
              : null;
          if (!sellerId || !sellerTotals[sellerId]) return;
          const summary = sellerTotals[sellerId];
          summary.leads += 1;
          if ((lead.aiScore || 0) >= 80) summary.hot += 1;
          const status = (lead.status || "nuevo").toLowerCase();
          if (status === "convertido") summary.won += 1;
          if (status !== "convertido" && status !== "inactivo") summary.active += 1;
        });

        invoicesSnap.docs.forEach((invDoc) => {
          const inv = invDoc.data();
          const clientId = invDoc.ref.parent.parent?.id;
          const total = parseFloat(inv.total || 0);
          const deposit = parseFloat(inv.deposit || 0);
          const payments = inv.payments || [];
          totalInvoiced += total;
          totalDeposits += deposit;

          const paymentsSum = payments.reduce(
            (sum, p) => sum + parseFloat(p.amount || 0),
            0
          );

          // Year comparison — solo cuenta si hay al menos un pago registrado
          const invCreatedAt = inv.createdAt?.toDate?.() || (inv.createdAt ? new Date(inv.createdAt) : null);
          const invYear = invCreatedAt instanceof Date && !isNaN(invCreatedAt) ? invCreatedAt.getFullYear() : null;
          if (paymentsSum > 0 && invYear === THIS_YEAR) {
            yearComp.invoiced.current += total;
            yearComp.invoiceCount.current++;
            yearComp.monthlyInvoiced.current[invCreatedAt.getMonth()] += total;
          } else if (paymentsSum > 0 && invYear === LAST_YEAR) {
            yearComp.invoiced.last += total;
            yearComp.invoiceCount.last++;
            yearComp.monthlyInvoiced.last[invCreatedAt.getMonth()] += total;
          }

          payments.forEach((p) => {
            const parsedAmount = parseFloat(p.amount || 0);
            if (!parsedAmount) return;
            const payDate = new Date(p.date);
            const payMonthKey = payDate.toLocaleString("es-PR", { month: "short", year: "numeric" });
            monthlyTotals[payMonthKey] = (monthlyTotals[payMonthKey] || 0) + parsedAmount;

            // Year comparison — payment year
            const payYear = payDate.getFullYear();
            const payMonth = payDate.getMonth();
            if (payYear === THIS_YEAR) {
              yearComp.collected.current += parsedAmount;
              yearComp.monthlyCollected.current[payMonth] += parsedAmount;
            } else if (payYear === LAST_YEAR) {
              yearComp.collected.last += parsedAmount;
              yearComp.monthlyCollected.last[payMonth] += parsedAmount;
            }
          });

          totalCollected += paymentsSum;


          const sellerKeyRaw = inv.sellerId || inv.createdBy?.uid || null;
          const sellerEmailKey = inv.createdBy?.email ? inv.createdBy.email.toLowerCase() : null;
          let sellerKey = null;
          if (sellerKeyRaw && sellerTotals[sellerKeyRaw]) {
            sellerKey = sellerKeyRaw;
          } else if (sellerEmailKey && sellerEmailMap[sellerEmailKey]) {
            sellerKey = sellerEmailMap[sellerEmailKey];
          }
          if (sellerKey && sellerTotals[sellerKey]) {
            sellerTotals[sellerKey].revenue += total;
            sellerTotals[sellerKey].collected += paymentsSum;
          }
        });
  
        setYearComparison(yearComp);

        // 🔹 Calcular propuestas aceptadas
        const proposals = proposalsSnap.docs.map((d) => d.data());
        const accepted = proposals.filter((p) =>
          ["aceptado", "aceptada"].includes(p.status)
        );
  
        // 🔹 Ordenar meses cronológicamente
        const orderedMonths = Object.keys(monthlyTotals).sort((a, b) => {
          const [ma, ya] = a.split(" ");
          const [mb, yb] = b.split(" ");
          const da = new Date(`${ma} 1, ${ya}`);
          const db = new Date(`${mb} 1, ${yb}`);
          return da - db;
        });
  
        const orderedValues = orderedMonths.map((m) => monthlyTotals[m]);
  
        // 🔹 Actualizar métricas
        setStats({
          proposalsSent: proposals.length,
          proposalsAccepted: accepted.length,
          clients: clientsSnap.size,
          leads: leadsSnap.size,
          totalInvoiced,
          totalDeposits,
          totalCollected,
        });
  
        // 🔹 Actualizar gráfica mensual
        setChartData({
          series: [{ name: "Pagos Recibidos", data: orderedValues }],
          options: {
            chart: { type: "area", toolbar: { show: false } },
            xaxis: { categories: orderedMonths },
            colors: ["#2563eb"],
            stroke: { curve: "smooth" },
          },
        });
  
        // 🔹 Cargar próximos eventos de la agenda (companyScheduleEvents —
        // misma colección que usan Agenda y la pestaña de agenda del cliente,
        // así que esto por fin refleja lo que de verdad está agendado).
        const now = Date.now();
        const eventList = eventsSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .map((event) => {
            const rawStart = event.startAt?.toDate?.() || (event.startAt ? new Date(event.startAt) : null);
            const startDate = rawStart instanceof Date && !isNaN(rawStart.getTime()) ? rawStart : null;
            return { ...event, startDate };
          })
          .filter((event) => event.status !== 'cancelled')
          .filter((event) => event.startDate && event.startDate.getTime() >= now - 24 * 3600 * 1000)
          .sort((a, b) => a.startDate - b.startDate)
          .slice(0, 6);

        setEvents(eventList);

        const ticketSummary = ticketsSnap.docs.reduce((acc, ticket) => {
          const data = ticket.data() || {};
          const status = (data.status || 'abierto').toLowerCase();
          const priority = (data.priority || '').toLowerCase();
          acc.total += 1;
          acc[status] = (acc[status] || 0) + 1;
          if (priority === 'critica') {
            acc.critica = (acc.critica || 0) + 1;
          }
          if (['abierto', 'en_proceso', 'en_espera_cliente'].includes(status)) {
            acc.openCount = (acc.openCount || 0) + 1;
          }
          return acc;
        }, { total: 0, openCount: 0 });

        setServiceStats(ticketSummary);

        setSellerPerformance(
          Object.values(sellerTotals).map((stat) => ({
            ...stat,
            conversionRate: stat.leads ? ((stat.won / stat.leads) * 100).toFixed(1) : "0.0",
            progress: stat.quota ? Math.min(100, (stat.revenue / stat.quota) * 100) : null,
          }))
        );

        const feed = timelineSnap.docs.map((docSnap) => {
          const data = docSnap.data() || {};
          const createdAt = data.createdAt?.toDate?.() || null;
          const leadRef = docSnap.ref.parent?.parent;
          const leadId = leadRef?.id || "Lead";
          return {
            id: docSnap.id,
            summary: data.summary || "Actualización en lead",
            channel: data.channel || "sistema",
            createdAt,
            lead: data.ticketTitle || data.leadName || data.lead || leadId,
            createdBy:
              typeof data.createdBy === "string"
                ? data.createdBy
                : data.createdBy?.email || data.createdBy?.name || "sistema",
          };
        });
        setActivityFeed(feed);
  
      } catch (err) {
        console.error("❌ Error cargando datos del dashboard:", err);
      }
    };
  
    loadData();
  }, [refreshKey]);
  
  useEffect(() => {
    const loadAIInsights = async () => {
      try {
        const leadsSnap = await getDocs(collection(db, "leads"));
        const leads = leadsSnap.docs.map((d) => d.data());
  
        if (leads.length === 0) {
          setAIInsights({
            totalLeads: 0,
            hotLeads: 0,
            avgScore: 0,
            topService: "-",
            conversionRate: 0,
          });
          setLeadMetrics({
            total: 0,
            nuevo: 0,
            en_proceso: 0,
            convertido: 0,
            inactivo: 0,
            promedioScore: 0,
            tasaConversion: 0,
            fuentes: {},
          });
          return;
        }
  
        // 🔹 Total de leads y leads calientes (score >= 80)
        const totalLeads = leads.length;
        const hotLeads = leads.filter((l) => (l.aiScore || 0) >= 80).length;
        const avgScore = leads.reduce((sum, l) => sum + (l.aiScore || 0), 0) / totalLeads;
  
        // 🔹 Servicio más solicitado
        const topService = findTopService(leads);
  
        // 🔹 Métricas adicionales por estado y fuente
        const metrics = {
          total: leads.length,
          nuevo: 0,
          en_proceso: 0,
          convertido: 0,
          inactivo: 0,
          promedioScore: 0,
          tasaConversion: 0,
          fuentes: {},
        };
  
        let totalScore = 0;
  
        leads.forEach((lead) => {
          const estado = (lead.status || "nuevo").toLowerCase();
          metrics[estado] = (metrics[estado] || 0) + 1;
  
          const fuente = (lead.source || "sin fuente").toLowerCase();
          metrics.fuentes[fuente] = (metrics.fuentes[fuente] || 0) + 1;
  
          totalScore += lead.aiScore || 0;
        });
  
        metrics.promedioScore = (totalScore / leads.length).toFixed(1);
        metrics.tasaConversion = ((metrics.convertido / leads.length) * 100).toFixed(1);
        metrics.leadsList = leads;
        const conversionRate = metrics.tasaConversion;

        // 🔹 Guardar ambos estados
        setAIInsights({
          totalLeads,
          hotLeads,
          avgScore: avgScore.toFixed(1),
          topService,
          conversionRate,
          
        });
  
        setLeadMetrics(metrics);
      } catch (err) {
        console.error("❌ Error cargando insights de IA y métricas:", err);
      }
    };
  
    loadAIInsights();
  }, []);

  const activityNotifications = useMemo(
    () =>
      activityFeed
        .map((item) => ({
          ...item,
          _key: buildActivityKey(item),
        }))
        .filter((item) => !dismissedActivities.has(item._key)),
    [activityFeed, dismissedActivities]
  );

  const unreadActivities = useMemo(
    () => activityNotifications.filter((item) => !readActivities.has(item._key)),
    [activityNotifications, readActivities]
  );

  const readActivitiesList = useMemo(
    () => activityNotifications.filter((item) => readActivities.has(item._key)),
    [activityNotifications, readActivities]
  );

  const displayedAILeads = useMemo(() => {
    if (!leadMetrics?.leadsList) return [];
    return leadMetrics.leadsList.slice(0, leadListExpanded ? 25 : 8);
  }, [leadMetrics?.leadsList, leadListExpanded]);

  const remainingAILeads = Math.max((leadMetrics?.leadsList?.length || 0) - displayedAILeads.length, 0);

  const markActivityRead = useCallback(
    (notification) => {
      if (!notification?._key) return;
      setReadActivities((prev) => {
        if (prev.has(notification._key)) return prev;
        const next = new Set(prev);
        next.add(notification._key);
        persistReadActivity(Array.from(next));
        return next;
      });
    },
    [persistReadActivity]
  );

  const dismissActivity = useCallback(
    (notification) => {
      if (!notification?._key) return;
      setDismissedActivities((prev) => {
        if (prev.has(notification._key)) return prev;
        const next = new Set(prev);
        next.add(notification._key);
        persistDismissedActivity(Array.from(next));
        return next;
      });
      setReadActivities((prev) => {
        if (!prev.has(notification._key)) return prev;
        const next = new Set(prev);
        next.delete(notification._key);
        persistReadActivity(Array.from(next));
        return next;
      });
    },
    [persistDismissedActivity, persistReadActivity]
  );

  const markAllActivitiesRead = useCallback(() => {
    if (!activityNotifications.length) return;
    setReadActivities((prev) => {
      const next = new Set(prev);
      let changed = false;
      activityNotifications.forEach(({ _key }) => {
        if (_key && !next.has(_key)) {
          next.add(_key);
          changed = true;
        }
      });
      if (changed) {
        persistReadActivity(Array.from(next));
        return next;
      }
      return prev;
    });
  }, [activityNotifications, persistReadActivity]);

  const dismissAllActivities = useCallback(() => {
    if (!activityNotifications.length) return;
    setDismissedActivities((prev) => {
      const next = new Set(prev);
      activityNotifications.forEach(({ _key }) => {
        if (_key) {
          next.add(_key);
        }
      });
      persistDismissedActivity(Array.from(next));
      return next;
    });
    setReadActivities((prev) => {
      if (!prev.size) return prev;
      const next = new Set(prev);
      activityNotifications.forEach(({ _key }) => {
        if (_key) {
          next.delete(_key);
        }
      });
      persistReadActivity(Array.from(next));
      return next;
    });
  }, [activityNotifications, persistDismissedActivity, persistReadActivity]);
  
  
  
  return (
    <div className="p-8 bg-gray-50 min-h-screen space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-semibold text-gray-800">
          Panel General de Actividad
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/admin/profile"
            className="inline-flex items-center gap-2 text-sm bg-white border border-gray-200 px-4 py-2 rounded-full shadow-sm hover:bg-gray-100"
          >
            <HiOutlineUserGroup className="w-4 h-4 text-orange-500" />
            Mi perfil
          </Link>
          <button
            type="button"
            onClick={() => setRefreshKey((prev) => prev + 1)}
            className="inline-flex items-center gap-2 text-sm bg-white border border-gray-200 px-4 py-2 rounded-full shadow-sm hover:bg-gray-100"
          >
            <HiOutlineArrowPath className="w-4 h-4" />
            Actualizar
          </button>
        </div>
      </div>

      <AttentionTodayPanel userId={user?.uid} />

      <WorkflowKpiPanel />

      {/* MÉTRICAS PRINCIPALES */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
        {/* Leads */}
        <div className="bg-white p-5 rounded-lg shadow hover:shadow-lg transition-all duration-200 border-l-4 border-blue-500 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Leads Totales</p>
            <p className="text-2xl font-bold text-gray-700">{stats.leads}</p>
          </div>
          <HiOutlineUserGroup className="text-blue-500 w-8 h-8" />
        </div>

        {/* Propuestas Enviadas */}
        <div className="bg-white p-5 rounded-lg shadow hover:shadow-lg transition-all duration-200 border-l-4 border-indigo-500 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Propuestas Enviadas</p>
            <p className="text-2xl font-bold text-gray-700">{stats.proposalsSent}</p>
          </div>
          <HiOutlineClipboardDocumentList className="text-indigo-500 w-8 h-8" />
        </div>

        {/* Propuestas Aceptadas */}
        <div className="bg-white p-5 rounded-lg shadow hover:shadow-lg transition-all duration-200 border-l-4 border-green-500 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Propuestas Aceptadas</p>
            <p className="text-2xl font-bold text-gray-700">{stats.proposalsAccepted}</p>
          </div>
          <HiOutlineChartBar className="text-green-500 w-8 h-8" />
        </div>

        {/* Clientes */}
        <div className="bg-white p-5 rounded-lg shadow hover:shadow-lg transition-all duration-200 border-l-4 border-purple-500 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Clientes Activos</p>
            <p className="text-2xl font-bold text-gray-700">{stats.clients}</p>
          </div>
          <HiOutlineUserGroup className="text-purple-500 w-8 h-8" />
        </div>

        {/* Ventas Totales */}
        <div className="bg-white p-5 rounded-lg shadow hover:shadow-lg transition-all duration-200 border-l-4 border-emerald-500 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Ventas Totales</p>
            <p className="text-2xl font-bold text-gray-700">
              ${formatMoney(stats.totalInvoiced)}
            </p>
          </div>
          <HiOutlineChartBar className="text-emerald-500 w-8 h-8" />
        </div>

        {/* Depósitos Registrados */}
        <div className="bg-white p-5 rounded-lg shadow hover:shadow-lg transition-all duration-200 border-l-4 border-yellow-500 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Depósitos Registrados</p>
            <p className="text-2xl font-bold text-gray-700">
              ${formatMoney(stats.totalDeposits)}
            </p>
          </div>
          <HiOutlineChartBar className="text-yellow-500 w-8 h-8" />
        </div>

        {/* Dinero Recibido */}
        <div className="bg-white p-5 rounded-lg shadow hover:shadow-lg transition-all duration-200 border-l-4 border-teal-500 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Dinero Recibido</p>
            <p className="text-2xl font-bold text-gray-700">
              ${formatMoney(stats.totalCollected)}
            </p>
          </div>
          <HiOutlineChartBar className="text-teal-500 w-8 h-8" />
        </div>

        <div className="bg-white p-5 rounded-lg shadow hover:shadow-lg transition-all duration-200 border-l-4 border-rose-500 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Tickets abiertos</p>
            <p className="text-2xl font-bold text-gray-700">{serviceStats.openCount || 0}</p>
            <p className="text-xs text-gray-500">
              Total: {serviceStats.total || 0} · Críticos: {serviceStats.critica || 0}
            </p>
          </div>
          <span className="text-rose-500 font-semibold text-lg">
            {serviceStats.en_proceso || 0}
          </span>
        </div>

        <div className="bg-white p-5 rounded-lg shadow hover:shadow-lg transition-all duration-200 border-l-4 border-orange-500 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Equipo BBTS</p>
            <p className="text-lg font-semibold text-gray-700">Invitar usuarios</p>
            <Link
              to="/admin/team"
              className="mt-2 inline-flex text-sm font-medium text-orange-600 hover:text-orange-700"
            >
              Abrir panel de equipo →
            </Link>
          </div>
          <HiOutlineUserGroup className="text-orange-500 w-8 h-8" />
        </div>

        <HostingMetricCards />
      </div>

      {/* GRÁFICA DE VENTAS */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-semibold text-gray-700 mb-3">
          Rendimiento de Ventas Mensual
        </h2>
        {chartData.series[0].data.length > 0 ? (
          <Chart
            options={chartData.options}
            series={chartData.series}
            type="area"
            height={300}
          />
        ) : (
          <p className="text-sm text-gray-500">No hay datos de ventas aún. Presiona “Actualizar” después de agregar facturas.</p>
        )}
      </div>

      {/* COMPARATIVA AÑO ACTUAL VS AÑO PASADO */}
      <div className="bg-white p-6 rounded-lg shadow space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-lg font-semibold text-gray-700">Comparativa {LAST_YEAR} vs {THIS_YEAR}</h2>
          <div className="flex items-center gap-3 text-xs font-semibold">
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-gray-300" />{LAST_YEAR}</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-orange-500" />{THIS_YEAR}</span>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            {
              label: 'Ventas con depósito recibido',
              current: yearComparison.invoiced.current,
              last: yearComparison.invoiced.last,
              prefix: '$',
            },
            {
              label: 'Dinero cobrado',
              current: yearComparison.collected.current,
              last: yearComparison.collected.last,
              prefix: '$',
            },
            {
              label: 'Facturas emitidas',
              current: yearComparison.invoiceCount.current,
              last: yearComparison.invoiceCount.last,
              prefix: '',
            },
          ].map((item) => {
            const diff = item.last > 0 ? (((item.current - item.last) / item.last) * 100).toFixed(1) : null;
            const isUp = diff === null ? null : parseFloat(diff) >= 0;
            return (
              <div key={item.label} className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                <p className="text-xs text-gray-500 mb-2">{item.label}</p>
                <div className="flex items-end justify-between gap-2">
                  <div>
                    <p className="text-2xl font-bold text-orange-600">
                      {item.prefix}{formatMoney(item.current)}
                    </p>
                    <p className="text-sm text-gray-400 mt-0.5">
                      {LAST_YEAR}: {item.prefix}{formatMoney(item.last)}
                    </p>
                  </div>
                  {diff !== null && (
                    <span className={`text-sm font-bold px-2 py-1 rounded-xl ${isUp ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                      {isUp ? '▲' : '▼'} {Math.abs(diff)}%
                    </span>
                  )}
                </div>
                {item.last > 0 && (
                  <div className="mt-3 h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-2 rounded-full bg-orange-400 transition-all duration-500"
                      style={{ width: `${Math.min(100, (item.current / Math.max(item.current, item.last)) * 100)}%` }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Gráfica de barras agrupada por mes */}
        <Chart
          type="bar"
          height={280}
          series={[
            { name: String(LAST_YEAR), data: yearComparison.monthlyCollected.last.map(v => parseFloat(v.toFixed(2))) },
            { name: String(THIS_YEAR), data: yearComparison.monthlyCollected.current.map(v => parseFloat(v.toFixed(2))) },
          ]}
          options={{
            chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
            plotOptions: { bar: { columnWidth: '60%', borderRadius: 4, grouped: true } },
            colors: ['#d1d5db', '#f97316'],
            xaxis: { categories: MONTH_LABELS, labels: { style: { fontSize: '11px' } } },
            yaxis: { labels: { formatter: (v) => `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}` } },
            tooltip: { y: { formatter: (v) => `$${formatMoney(v)}` } },
            legend: { position: 'top', horizontalAlign: 'right', fontSize: '12px' },
            dataLabels: { enabled: false },
            grid: { borderColor: '#f3f4f6' },
          }}
        />
      </div>

      {sellerPerformance.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">Rendimiento de vendedores</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                <tr>
                  <th className="px-4 py-2 text-left">Vendedor</th>
                  <th className="px-4 py-2 text-center">Leads</th>
                  <th className="px-4 py-2 text-center">Hot</th>
                  <th className="px-4 py-2 text-center">Ganados</th>
                  <th className="px-4 py-2 text-center">Conversión</th>
                  <th className="px-4 py-2 text-right">Ventas</th>
                  <th className="px-4 py-2 text-right">Meta</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sellerPerformance.map((seller) => (
                  <tr key={seller.id}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-gray-900">{seller.name}</p>
                      <p className="text-xs text-gray-500">{seller.email}</p>
                    </td>
                    <td className="px-4 py-3 text-center">{seller.leads}</td>
                    <td className="px-4 py-3 text-center text-orange-600">{seller.hot}</td>
                    <td className="px-4 py-3 text-center text-emerald-600">{seller.won}</td>
                    <td className="px-4 py-3 text-center">
                      {seller.conversionRate || "0.0"}%
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">
                      ${formatMoney(seller.revenue)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {seller.quota ? `$${formatMoney(seller.quota)}` : "—"}
                      {seller.progress != null && (
                        <div className="w-full bg-gray-100 rounded-full h-2 mt-1">
                          <div
                            className="h-2 rounded-full bg-orange-500"
                            style={{ width: `${seller.progress}%` }}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* EVENTOS + NOTIFICACIONES — apilados a todo ancho, Eventos arriba
          (compacto) y Notificaciones abajo (expandida, no comprimida en una
          columna angosta). */}
      <div className="flex flex-col gap-6">
        {/* Eventos — lista compacta de una línea, igual de densa que
            Atención Hoy. */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-gray-500">
              <HiOutlineCalendarDays className="h-4 w-4 text-blue-500" /> Próximos Eventos
            </h2>
            <Link to="/admin/calendar" className="text-xs font-semibold text-orange-600 hover:underline">
              Ver agenda →
            </Link>
          </div>
          {events.length > 0 ? (
            <ul className="grid gap-1.5 sm:grid-cols-2">
              {events.slice(0, 6).map((e) => {
                const typeMeta = getScheduleTypeMeta(e.type);
                return (
                  <li key={e.id || e.title}>
                    <Link
                      to="/admin/calendar"
                      className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50/60 px-3 py-2 text-sm hover:bg-gray-100"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="flex-shrink-0 text-xs font-semibold text-blue-600">
                          {e.startDate?.toLocaleDateString("es-PR", { day: "2-digit", month: "short" }) || "—"}
                        </span>
                        <span className="truncate font-medium text-gray-800">{e.title || "Evento sin título"}</span>
                      </span>
                      <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[0.65rem] font-semibold ${typeMeta.chip}`}>
                        {typeMeta.label}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">No hay eventos registrados.</p>
          )}
        </div>

        {/* Notificaciones */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-700">Actividad reciente de leads</h2>
              <p className="text-sm text-gray-500">Historial de cambios en leads. Las alertas que requieren atención están arriba, en Indicadores operativos.</p>
            </div>
            <div className="flex flex-wrap gap-2 text-xs sm:text-sm">
              {activityNotifications.length > 0 && (
                <button
                  type="button"
                  onClick={dismissAllActivities}
                  className="font-semibold text-gray-500 hover:text-gray-700"
                >
                  Ocultar todo
                </button>
              )}
              {unreadActivities.length > 0 && (
                <button
                  type="button"
                  onClick={markAllActivitiesRead}
                  className="font-semibold text-orange-600 hover:text-orange-700"
                >
                  Marcar todo como leído
                </button>
              )}
            </div>
          </div>
          {activityNotifications.length === 0 ? (
            <p className="mt-4 text-sm text-gray-500">Sin movimientos recientes.</p>
          ) : (
            <div className="mt-4 space-y-4">
              {unreadActivities.length > 0 ? (
                <ul className="divide-y divide-gray-200 rounded-xl border border-gray-100 text-sm overflow-hidden">
                  {unreadActivities.map((item) => (
                    <li
                      key={item._key}
                      className="flex flex-col gap-2 px-4 py-3 bg-orange-50/40 md:flex-row md:items-center md:justify-between"
                    >
                      <div>
                        <p className="font-semibold text-gray-900">{item.summary}</p>
                        <p className="text-xs text-gray-500">
                          Lead: {item.lead || "Sin nombre"} · Canal: {item.channel}
                        </p>
                        <p className="text-xs text-gray-400">
                          {item.createdBy ? `Por ${item.createdBy}` : "Sistema"} · {formatFullDate(item.createdAt)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <button
                          type="button"
                          onClick={() => markActivityRead(item)}
                          className="font-semibold text-orange-600 hover:text-orange-700"
                        >
                          Marcar leído
                        </button>
                        <button
                          type="button"
                          onClick={() => dismissActivity(item)}
                          className="font-semibold text-gray-500 hover:text-gray-700"
                        >
                          Ocultar
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500">No hay actividad nueva de leads.</p>
              )}

              {readActivitiesList.length > 0 && (
                <div className="rounded-xl border border-dashed border-gray-200 p-4">
                  <button
                    type="button"
                    onClick={() => setShowReadActivities((prev) => !prev)}
                    className="flex w-full items-center justify-between text-sm font-medium text-gray-600"
                  >
                    <span>
                      {showReadActivities ? "Ocultar" : "Mostrar"} notificaciones confirmadas ({readActivitiesList.length})
                    </span>
                    <span className="text-xs text-gray-400">{showReadActivities ? "▲" : "▼"}</span>
                  </button>
                  {showReadActivities && (
                    <ul className="mt-3 divide-y divide-gray-100 text-sm">
                      {readActivitiesList.map((item) => (
                        <li
                          key={`${item._key}-read`}
                          className="flex flex-col gap-2 py-2 md:flex-row md:items-center md:justify-between"
                        >
                          <div>
                            <p className="font-semibold text-gray-800">{item.summary}</p>
                            <p className="text-xs text-gray-500">
                              Lead: {item.lead || "Sin nombre"} · Canal: {item.channel}
                            </p>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-400">
                            <span>{formatFullDate(item.createdAt)}</span>
                            <button
                              type="button"
                              onClick={() => dismissActivity(item)}
                              className="font-semibold text-gray-500 hover:text-gray-700"
                            >
                              Ocultar
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* INTELIGENCIA COMERCIAL */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold text-gray-700 mb-4">Inteligencia Comercial (AI)</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {[
            { label: "Total Leads", value: aiInsights.totalLeads, color: "text-blue-700", bg: "bg-blue-50" },
            { label: "Leads Calientes", value: aiInsights.hotLeads, color: "text-orange-600", bg: "bg-orange-50" },
            { label: "Intención Promedio", value: `${aiInsights.avgScore}%`, color: "text-indigo-700", bg: "bg-indigo-50" },
            { label: "Tasa de Conversión", value: `${aiInsights.conversionRate}%`, color: "text-emerald-700", bg: "bg-emerald-50" },
            { label: "Servicio Popular", value: aiInsights.topService, color: "text-gray-900", bg: "bg-gray-100" },
          ].map((item) => (
            <div key={item.label} className={`${item.bg} rounded-2xl p-4 text-center`}>
              <p className="text-xs text-gray-500 mb-1">{item.label}</p>
              <p className={`text-xl font-bold ${item.color} truncate`}>{item.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* RESUMEN DE LEADS */}
      {leadMetrics && (
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-700 mb-3">Resumen de Leads por Estado</h2>
            <div className="flex snap-x gap-3 overflow-x-auto pb-2">
              {[
                { label: "Total Leads", value: leadMetrics.total, accent: "border-orange-500" },
                { label: "Nuevos", value: leadMetrics.nuevo, accent: "border-yellow-500" },
                { label: "En proceso", value: leadMetrics.en_proceso, accent: "border-blue-500" },
                { label: "Convertidos", value: leadMetrics.convertido, accent: "border-green-500" },
                { label: "Inactivos", value: leadMetrics.inactivo, accent: "border-gray-400" },
                { label: "Promedio Score", value: leadMetrics.promedioScore, accent: "border-indigo-500" },
                { label: "Tasa de Conversión", value: `${leadMetrics.tasaConversion}%`, accent: "border-emerald-500" },
              ].map((card) => (
                <div
                  key={card.label}
                  className={`min-w-[150px] rounded-2xl border border-gray-100 bg-white px-4 py-3 shadow-sm border-l-4 ${card.accent}`}
                >
                  <p className="text-xs text-gray-500">{card.label}</p>
                  <p className="text-xl font-semibold text-gray-800">{card.value}</p>
                </div>
              ))}
            </div>
          </div>

          {displayedAILeads.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-700">Seguimiento automático de leads (AI)</h3>
                  <p className="text-sm text-gray-500">Vista compacta de los últimos movimientos.</p>
                </div>
                {remainingAILeads > 0 && (
                  <button
                    type="button"
                    onClick={() => setLeadListExpanded((prev) => !prev)}
                    className="text-sm font-semibold text-orange-600 hover:text-orange-700"
                  >
                    {leadListExpanded ? "Ver menos" : `Ver ${remainingAILeads} más`}
                  </button>
                )}
              </div>
              <div className="mt-4 overflow-x-auto">
                <div className="max-h-96 overflow-y-auto rounded-xl border border-gray-100">
                  <table className="min-w-full text-sm text-left text-gray-600">
                    <thead className="sticky top-0 bg-gray-50 text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-4 py-2">Nombre</th>
                        <th className="px-4 py-2">Canal</th>
                        <th className="px-4 py-2">Score</th>
                        <th className="px-4 py-2">Prioridad</th>
                        <th className="px-4 py-2">Último contacto</th>
                        <th className="px-4 py-2">Mensaje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedAILeads.map((lead, i) => (
                        <tr key={`${lead.id || lead.name || i}`} className="border-b hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-2 font-medium text-gray-900">{lead.name || "Sin nombre"}</td>
                          <td className="px-4 py-2 capitalize">{lead.aiChannel || lead.source || "-"}</td>
                          <td className="px-4 py-2 font-semibold text-orange-600">{lead.aiScore || 0}</td>
                          <td className="px-4 py-2 capitalize">{lead.prioridad || "-"}</td>
                          <td className="px-4 py-2">{formatFullDate(lead.lastContactAt)}</td>
                          <td className="px-4 py-2 text-xs text-gray-500">
                            {lead.lastContactNote?.slice(0, 120) || "Sin seguimiento"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
